/**
 * SwitchCaseInspector — Phase 5a-6.
 *
 * Edits the switch head config: an expression to evaluate + the list
 * of cases. Cases land here as a JSON textarea in the Phase 5a-6 MVP;
 * a key-value editor (one row per case with inline value/label fields)
 * is a Phase 6 polish item.
 *
 * The per-branch + button (Phase 2b) already handles adding nodes
 * INTO a case from the canvas; this inspector edits the cases
 * themselves (their values and labels), not their bodies.
 *
 * Spread-preservation contract identical to prior editors.
 */

import React from 'react';

export default function SwitchCaseInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const expression = cfg.expression || '';
  const cases = cfg.cases;
  const casesStr = Array.isArray(cases)
    ? JSON.stringify(cases, null, 2)
    : (typeof cases === 'string' ? cases : '');

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  const setCases = (raw) => {
    if (raw.trim() === '') return setField('cases', undefined);
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) setField('cases', parsed);
      else setField('cases', raw);
    } catch (_) {
      setField('cases', raw); // keep raw so user can fix syntax
    }
  };

  // Count cases for the port summary footer
  const caseCount = Array.isArray(cases) ? cases.length : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Expression</span>
        <input
          type="text"
          value={expression}
          onChange={(e) => setField('expression', e.target.value)}
          placeholder="input.severity"
          disabled={readOnly}
          style={inputStyle}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>
          Expression to evaluate. Match against each case’s <code style={inlineCode}>value</code>.
        </span>
      </label>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>Cases</span>
        <textarea
          value={casesStr}
          onChange={(e) => setCases(e.target.value)}
          placeholder='[{"value":"critical","label":"Case 1"},{"value":"high","label":"Case 2"}]'
          disabled={readOnly}
          rows={6}
          style={textareaStyle}
        />
        <span style={{ fontSize: 10, color: '#64748b' }}>
          JSON array of <code style={inlineCode}>{`{value, label}`}</code> objects.
          Phase 6 will add a key-value editor.
        </span>
      </label>

      <div style={{
        padding: 10,
        background: '#f1f5f9',
        borderRadius: 6,
        fontSize: 11,
        color: '#0f172a',
        lineHeight: 1.6,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>
          Branches ({caseCount} case{caseCount === 1 ? '' : 's'} + default)
        </div>
        <div style={{ fontSize: 10, color: '#64748b' }}>
          Wire each branch on the canvas. Use the orange <strong>+</strong>
          button on a case container to insert a node into that branch.
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '6px 8px',
  fontSize: 12, color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1', borderRadius: 4,
  outline: 'none', fontFamily: 'inherit',
};
const textareaStyle = { ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11, lineHeight: 1.5 };
const inlineCode = {
  background: '#fff', padding: '0 4px', borderRadius: 3,
  fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 10,
  border: '0.5px solid #cbd5e1',
};
